// Prévisualisation d'un document produit par le moteur (CV, vue avant/après,
// lettre) — le composant partagé entre l'extension et la console.
//
// PRINCIPE, repris de l'extension : le moteur produit un document HTML
// AUTONOME (doctype + styles inclus), qu'on injecte tel quel dans une iframe
// via `srcDoc`. Deux conséquences qui justifient à elles seules ce choix :
//
//  1. Le rendu est IDENTIQUE partout. La console et l'extension affichent le
//     même CV parce que c'est le même code qui le produit — pas deux
//     réimplémentations qui finiraient par diverger.
//  2. Le PDF est l'impression native de l'iframe : `contentWindow.print()`
//     n'imprime QUE le document de l'iframe. Aucun moteur PDF, aucune
//     dépendance.
//
// L'iframe est sandboxée SANS `allow-scripts` — contrainte de l'extension, où
// la CSP du site hôte bloquerait de toute façon un script embarqué. Toute
// l'interactivité est donc pilotée depuis le parent, qui accède au DOM de
// l'iframe grâce à `allow-same-origin`.

import { useCallback, useEffect, useRef } from 'react'
import { printFrame } from './printFrame'

export interface PreviewDoc {
  /** Identifiant stable de l'onglet. */
  key: string
  label: string
  /** Document HTML autonome, tel que rendu par le moteur. */
  html: string
  /** Simuler la pagination A4 (documents de type CV). */
  paginate?: boolean
}

export interface DocumentPreviewProps {
  docs: PreviewDoc[]
  activeKey: string
  onSelect: (key: string) => void
  /** Boutons propres à l'app, rendus à gauche du bouton d'impression. */
  actions?: React.ReactNode
  height?: string
  labels?: { print: string; empty: string }
  /** Découpe le document en feuilles A4 — `paginatePreview` du moteur.
   *  Injecté plutôt qu'importé : le composant reste utilisable sans. */
  paginate?: (doc: Document) => void
  /** Accès au document de l'iframe une fois chargé, pour brancher des
   *  comportements propres à l'app (ex. les boutons « rétablir » du diff). */
  onDocumentReady?: (doc: Document) => void
}

const DEFAULT_LABELS = { print: 'Imprimer / PDF', empty: 'Aucun document à afficher.' }

export function DocumentPreview({
  docs,
  activeKey,
  onSelect,
  actions,
  height = '70vh',
  labels = DEFAULT_LABELS,
  paginate,
  onDocumentReady,
}: DocumentPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const active = docs.find((d) => d.key === activeKey) ?? docs[0]

  const wire = useCallback(
    (doc: Document) => {
      // Reproduit depuis le parent le comportement du script que le moteur
      // embarque dans la vue diff : cliquer une phrase reformulée déplie son
      // texte d'origine. Le script embarqué ne tourne pas (pas d'allow-scripts).
      doc.addEventListener('click', (e) => {
        let node = e.target as HTMLElement | null
        while (node && node !== doc.body) {
          if (node.classList?.contains('diff-reword')) {
            const li = node.closest('li')
            ;(li ?? node).classList.toggle('open')
            return
          }
          node = node.parentElement
        }
      })
      if (active?.paginate && paginate) paginate(doc)
      onDocumentReady?.(doc)
    },
    [active?.paginate, active?.key, paginate, onDocumentReady]
  )

  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return
    const onLoad = () => {
      frame.contentWindow?.scrollTo(0, 0)
      const doc = frame.contentDocument
      if (doc) wire(doc)
    }
    frame.addEventListener('load', onLoad)
    // Si le document est déjà chargé (re-render sans changement de srcDoc),
    // `load` ne se redéclenchera pas.
    if (frame.contentDocument?.readyState === 'complete') onLoad()
    return () => frame.removeEventListener('load', onLoad)
  }, [active?.html, wire])

  function print() {
    printFrame(frameRef.current?.contentWindow)
  }

  if (!active) return <div className="preview-empty">{labels.empty}</div>

  return (
    <div className="preview">
      <div className="preview-bar">
        {docs.length > 1 && (
          <div className="preview-tabs" role="tablist">
            {docs.map((d) => (
              <button
                key={d.key}
                role="tab"
                aria-selected={d.key === active.key}
                className={`preview-tab${d.key === active.key ? ' active' : ''}`}
                onClick={() => onSelect(d.key)}
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        <div className="preview-spacer" />
        {actions}
        <button className="btn btn-ghost" onClick={print}>
          {labels.print}
        </button>
      </div>
      <iframe
        ref={frameRef}
        className="preview-frame"
        style={{ height }}
        title={active.label}
        srcDoc={active.html}
        sandbox="allow-same-origin allow-modals"
      />
    </div>
  )
}
